/**
 * Public Tool Registry — single registration point.
 *
 * Add a new tool by:
 *   1. Creating `manifests/<slug>.ts`
 *   2. Importing + adding it to `ALL_TOOL_MANIFESTS` below
 *
 * Hub grid, ToolHeader, sitemap, Schema.org and internal tool links all
 * derive from this registry.
 */
import type { ToolManifest } from './types';

import naceAraci from './manifests/nace-araci';
import yasalYukumluluk from './manifests/yasal-yukumluluk-hesaplayici';
import lotoProseduru from './manifests/loto-proseduru';
import kkdSecim from './manifests/kkd-secim-yardimcisi';
import ekipmanKontrol from './manifests/ekipman-kontrol-listesi';
import isgTakvim from './manifests/isg-takvim';

const ALL_TOOL_MANIFESTS: ToolManifest[] = [
  naceAraci,
  yasalYukumluluk,
  lotoProseduru,
  kkdSecim,
  ekipmanKontrol,
  isgTakvim,
];

/**
 * Hub + sitemap için sıralı, enabled liste.
 * Disabled manifest'ler hub/sitemap'te gizli — internal preview için
 * `getToolBySlug` ile manuel erişilebilir.
 */
export const PUBLIC_TOOLS: ToolManifest[] = ALL_TOOL_MANIFESTS
  .filter((t) => t.enabled)
  .sort((a, b) => a.order - b.order);

/**
 * Tüm manifest'ler (test, admin, audit için).
 */
export const ALL_TOOLS: ToolManifest[] = [...ALL_TOOL_MANIFESTS];

export function getToolBySlug(slug: string): ToolManifest | undefined {
  return ALL_TOOL_MANIFESTS.find((t) => t.slug === slug);
}

export function getToolByHref(href: string): ToolManifest | undefined {
  return ALL_TOOL_MANIFESTS.find((t) => t.href === href);
}

/**
 * ToolHeader desktop için kısa etiketler (ilk 4 araç + "Tüm Araçlar").
 * Kalan araçlar mobile sheet'te tam liste olarak görünür.
 */
export function getDesktopHeaderLinks(): Array<{ href: string; label: string }> {
  return [
    { href: '/araclar', label: 'Tüm Araçlar' },
    ...PUBLIC_TOOLS.slice(0, 4).map((t) => ({
      href: t.href,
      label: t.shortLabel,
    })),
  ];
}

export function getMobileHeaderLinks(): Array<{ href: string; label: string }> {
  return [
    { href: '/araclar', label: 'Tüm Araçlar' },
    ...PUBLIC_TOOLS.map((t) => ({ href: t.href, label: t.title })),
  ];
}
